import type { JSX } from "react";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReplayIcon from "@mui/icons-material/Replay";
import DescriptionIcon from "@mui/icons-material/Description";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GridViewIcon from "@mui/icons-material/GridView";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

/**
 * Friendly aliases + icons + colors for QuickBooks Desktop transaction
 * types. The QB Web Connector ships "CreditCardCharge", "CreditCardCredit",
 * etc.; users see a friendlier vocabulary on the dashboard and tabs.
 *
 * Used by the Transactions page tabs (filter by type) and the Dashboard
 * type-breakdown donut. Keep keys in sync with `qbTransactions.txnType`
 * values populated by the qbwc-bridge response-dispatcher.
 */

export type QbTypeKey =
  | "all"
  | "CreditCardCharge"
  | "CreditCardCredit"
  | "Check"
  | "Deposit"
  | "Bill"
  | "JournalEntry"
  | "not_posted";

export interface QbTypeDef {
  key: QbTypeKey;
  /** Customer-facing label, short enough for a tab. */
  label: string;
  /** A few words describing what this type means in plain English. */
  description: string;
  /** MUI icon component reference (already instantiated). */
  Icon: typeof CreditCardIcon;
  /** Accent color (CSS color or var()). Used on chips, donut, tab indicator. */
  color: string;
  /** Whether this slice belongs in the type-breakdown donut. */
  inBreakdown: boolean;
}

export const QB_TYPE_DEFS: Record<QbTypeKey, QbTypeDef> = {
  all: {
    key: "all",
    label: "All Types",
    description: "Every transaction in this cycle, regardless of QB type",
    Icon: GridViewIcon,
    color: "#6c5ce7",
    inBreakdown: false,
  },
  CreditCardCharge: {
    key: "CreditCardCharge",
    label: "Card Charges",
    description: "Purchases booked to the BOA credit card",
    Icon: CreditCardIcon,
    color: "#fd79a8",
    inBreakdown: true,
  },
  CreditCardCredit: {
    key: "CreditCardCredit",
    label: "Card Refunds",
    description: "Refunds and credits applied to the credit card",
    Icon: ReplayIcon,
    color: "#a29bfe",
    inBreakdown: true,
  },
  Check: {
    key: "Check",
    label: "Checks",
    description: "Bank-side withdrawals booked as checks",
    Icon: DescriptionIcon,
    color: "#74b9ff",
    inBreakdown: true,
  },
  Deposit: {
    key: "Deposit",
    label: "Deposits",
    description: "Money received into a bank account",
    Icon: TrendingUpIcon,
    color: "#55efc4",
    inBreakdown: true,
  },
  Bill: {
    key: "Bill",
    label: "Vendor Bills",
    description: "Accounts Payable bills with vendor + due date",
    Icon: ReceiptLongIcon,
    color: "#ffeaa7",
    inBreakdown: true,
  },
  JournalEntry: {
    key: "JournalEntry",
    label: "Journal Entries",
    description: "Inter-account transfers and accounting adjustments",
    Icon: MenuBookIcon,
    color: "#fdcb6e",
    inBreakdown: true,
  },
  not_posted: {
    key: "not_posted",
    label: "Not Posted",
    description: "Rows not yet in QuickBooks Desktop",
    Icon: HourglassEmptyIcon,
    color: "#b2bec3",
    inBreakdown: false,
  },
};

/** Display order for tabs. "All" first, "Not Posted" last. */
export const QB_TYPE_TAB_ORDER: QbTypeKey[] = [
  "all",
  "CreditCardCharge",
  "CreditCardCredit",
  "Check",
  "Deposit",
  "Bill",
  "JournalEntry",
  "not_posted",
];

/**
 * Friendly label for a raw qbTransactions.txnType value, or null/undefined
 * if the row hasn't been posted yet.
 */
export function aliasFor(txnType: string | null | undefined): string {
  if (!txnType) return QB_TYPE_DEFS.not_posted.label;
  const def = QB_TYPE_DEFS[txnType as QbTypeKey];
  return def?.label ?? txnType;
}
